import { Button, Drawer, Segmented, Skeleton } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { MdOutlineDoneAll, MdOutlineNotificationsNone } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import { DISK_OFFBOARDING_ENTITY_CODE, diskAdminOffboardingUrl } from '@/Containers/DiskSection/DiskAdminContainer/Hooks'
import { DISK_NODE_ENTITY_CODE, diskNodeUrl } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { classNames } from '@/Ship/Helpers/classNames'
import { formatDayLabel } from '@/Ship/Helpers/fieldViews'

import { notificationEntity } from '../Helpers/notificationView'
import { NotificationStore } from '../Stores/NotificationStore'
import { LoadNotificationListTask } from '../Tasks/LoadNotificationListTask'
import { LoadUnreadCountTask } from '../Tasks/LoadUnreadCountTask'
import { MarkAllNotificationsReadTask } from '../Tasks/MarkAllNotificationsReadTask'
import { MarkNotificationsReadTask } from '../Tasks/MarkNotificationsReadTask'
import { NotificationListItem } from './NotificationListItem'

import type { Notification, NotificationFilter } from '../Stores/NotificationStore'

import styles from './Notifications.module.css'

/** Ширина шухляди. У колишніх 414 px назва запису обрізалася на півслові. */
const DRAWER_WIDTH = 560

interface Day {
    key: string
    label: string
    items: Notification[]
}

/** Сусідні сповіщення одного дня — в один блок. Бек віддає новіші зверху. */
const groupByDay = (items: Notification[]): Day[] => {
    const days: Day[] = []

    for (const item of items) {
        const date = item.createdAt ? dayjs(item.createdAt) : null
        const key = date?.isValid() ? date.format('YYYY-MM-DD') : 'unknown'
        const last = days[days.length - 1]

        if (last?.key === key) {
            last.items.push(item)
        } else {
            days.push({ key, label: date?.isValid() ? formatDayLabel(date) : 'Без дати', items: [item] })
        }
    }

    return days
}

const reload = (): void => {
    NotificationStore.reset()
    void LoadNotificationListTask(1)
}

/** Непрочитані сповіщення про той самий запис, що й `item`, — разом із ним. */
const sameEntityUnread = (item: Notification): string[] =>
    NotificationStore.notifications
        .filter(
            (other) =>
                !other.isReaded &&
                Boolean(other.entityUuid) &&
                other.entityUuid === item.entityUuid &&
                other.entityCode === item.entityCode
        )
        .map((other) => other.uuid)

/**
 * Сповіщення користувача — шухляда з дзвіночка в шапці.
 *
 * Клік по сповіщенню відкриває запис поверх шухляди й позначає прочитаними
 * всі сповіщення про цей запис: усе, про що вони, видно в його картці.
 * Шухляда при цьому лишається відкритою — закривши картку, людина
 * повертається до стрічки й іде далі.
 */
export const NotificationDrawer = observer(() => {
    const store = NotificationStore
    const navigate = useNavigate()
    const days = useMemo(() => groupByDay(store.notifications), [store.notifications])

    useEffect(() => {
        if (!store.isDrawerOpen) return

        reload()
        void LoadUnreadCountTask()
    }, [store.isDrawerOpen])

    const changeFilter = (value: NotificationFilter) => {
        store.setFilter(value)
        reload()
    }

    const open = (item: Notification) => {
        void MarkNotificationsReadTask(Array.from(new Set([...(item.isReaded ? [] : [item.uuid]), ...sameEntityUnread(item)])))

        const entity = notificationEntity(item)
        if (!entity) return

        // Вузол диска — не ресурс із карткою: екран диска за посиланням на вузол
        if (entity.resource === DISK_NODE_ENTITY_CODE) {
            store.setIsDrawerOpen(false)
            navigate(diskNodeUrl(entity.uuid))

            return
        }

        // Справа звільнення — сторінка адмін-панелі диска, а не картка ресурсу
        if (entity.resource === DISK_OFFBOARDING_ENTITY_CODE) {
            store.setIsDrawerOpen(false)
            navigate(diskAdminOffboardingUrl(entity.uuid))

            return
        }

        OpenResourceCardTask({ resource: entity.resource, primaryFieldValue: entity.uuid })
    }

    const isEmpty = store.notifications.length === 0

    return (
        <Drawer
            size={Math.min(DRAWER_WIDTH, window.innerWidth)}
            title="Сповіщення"
            open={store.isDrawerOpen}
            onClose={() => store.setIsDrawerOpen(false)}
            extra={
                <Button
                    type="text"
                    icon={<MdOutlineDoneAll style={{ fontSize: 18 }} />}
                    disabled={store.unreadCount === 0}
                    loading={store.isMarkingAll}
                    onClick={() => void MarkAllNotificationsReadTask()}
                >
                    Прочитати всі
                </Button>
            }
            styles={{ body: { padding: 0 } }}
        >
            <div className={classNames(styles['body'])}>
                <div className={classNames(styles['toolbar'])}>
                    <Segmented<NotificationFilter>
                        value={store.filter}
                        onChange={changeFilter}
                        options={[
                            { label: 'Усі', value: 'all' },
                            {
                                label: store.unreadCount > 0 ? `Непрочитані · ${store.unreadCount}` : 'Непрочитані',
                                value: 'unread',
                            },
                        ]}
                    />
                </div>

                {isEmpty && store.isLoadingFirstPage && (
                    <div className={classNames(styles['group'])} style={{ padding: 16 }}>
                        {[0, 1, 2, 3].map((index) => (
                            <Skeleton key={index} active avatar={{ size: 36 }} title={false} paragraph={{ rows: 2 }} />
                        ))}
                    </div>
                )}

                {isEmpty && !store.isLoadingFirstPage && (
                    <div className={classNames(styles['empty'])}>
                        <span className={classNames(styles['emptyIcon'])}>
                            <MdOutlineNotificationsNone />
                        </span>
                        <div className={classNames(styles['emptyTitle'])}>
                            {store.filter === 'unread' ? 'Усе прочитано' : 'Сповіщень ще немає'}
                        </div>
                        {store.filter === 'all' && (
                            <div className={classNames(styles['emptyText'])}>
                                Тут з'являтимуться призначення й прострочення ваших справ, запити на верифікацію та помилки відправки листів.
                            </div>
                        )}
                    </div>
                )}

                {days.map((day) => (
                    <section key={day.key} className={classNames(styles['day'])}>
                        <div className={classNames(styles['dayLabel'])}>{day.label}</div>
                        <div className={classNames(styles['group'])}>
                            {day.items.map((item) => (
                                <NotificationListItem
                                    key={item.uuid}
                                    item={item}
                                    onOpen={open}
                                    onMarkRead={(target) => void MarkNotificationsReadTask([target.uuid])}
                                />
                            ))}
                        </div>
                    </section>
                ))}

                {store.hasMore && (
                    <Button
                        block
                        className={classNames(styles['more'])}
                        loading={store.isLoadingMore}
                        onClick={() => void LoadNotificationListTask(store.page + 1)}
                    >
                        Показати ще
                    </Button>
                )}
            </div>
        </Drawer>
    )
})
